/* Build in libraries */
import { useState } from 'react';

/* Third party libraries */

/* Components */
import AffordableUnitsCreatedWidget from 'components/Rover/Widgets/AffordableUnitsCreatedWidget';
import ApprovedProjectsWidget from 'components/Rover/Widgets/ApprovedProjectsWidget';
import BlightedPropertiesEliminatedWidget from 'components/Rover/Widgets/BlightedProjectEliminatedWidget';
import HousingUnitsCreatedWidget from 'components/Rover/Widgets/HousingUnitsCreatedWidget';
import NewJobCreatedByClassification from 'components/Rover/Widgets/JobsCreatedByClassification';
import NewJobCreatedWidget from 'components/Rover/Widgets/NewJobsCreatedWidget';
import TotalAmountRequested from 'components/Rover/Widgets/TotalAmountRequested';
import TotalApplicationsWidget from 'components/Rover/Widgets/TotalApplicationsWidget';
import TotalBuildingsWidget from 'components/Rover/Widgets/TotalBuildingsWidget';
import TotalProgramFundsRemainingWidget from 'components/Rover/Widgets/TotalProgramFundsRemainingWidget';
import TotalProgramFundsWidget from 'components/Rover/Widgets/TotalProgramFundsWidget';
import TotalPropertiesReturnedToUseWidget from 'components/Rover/Widgets/TotalPropertiesReturnedToUseWidget';
import TotalValueOfAllProjectsWidget from 'components/Rover/Widgets/TotalValueOfAllProjectsWidget';
import Card from 'components/UI/Card';

/* Services */

/* Assets */
import { EApplicationStatus } from 'features/ApplicationForm/constants';
import { convertEnumToText, isObjectEmpty } from 'utils';

interface IApplicationDashboardComponentProps {
  data: any;
}

export const BFIPDashboard = ({
  data,
}: IApplicationDashboardComponentProps) => {
  const applicationsList = data?.data;

  const widgetsData: {
    totalApplications: number;
    totalBuildings: number;
    totalAmountRequested: number;
    totalProgramFunds: number;
    totalApprovedProjects: number;
    totalProgramFundsRemaining: number;
    totalValueOfAllProjects: number;
    blightedPropertiesEliminated: number;
    totalPropertiesReturnedToUse: number;
    totalHousingUnitsCreated: number;
    totalAffordableUnitsCreated: number;
    totalNewJobsCreated: number;
    statusSummary: { [key: string]: number };
    newJobsCreatedByClassification: { [key: string]: number };
  } = {
    totalApplications: 0,
    totalBuildings: 0,
    totalAmountRequested: 0,
    totalProgramFunds: 0,
    totalApprovedProjects: 0,
    totalProgramFundsRemaining: 0,
    totalValueOfAllProjects: 0,
    blightedPropertiesEliminated: 0,
    totalPropertiesReturnedToUse: 0,
    totalHousingUnitsCreated: 0,
    totalAffordableUnitsCreated: 0,
    totalNewJobsCreated: 0,
    statusSummary: {},
    newJobsCreatedByClassification: {},
  };

  applicationsList?.forEach((a: any) => {
    const { applicationData } = a;

    //Total Application Widget
    widgetsData['totalApplications'] += 1;

    // Total Amount Requested
    widgetsData['totalAmountRequested'] +=
      applicationData?.step3?.section2.reduce((acc: any, curr: any) => {
        return (acc += curr ? curr?.totalCost : 0);
      }, 0);

    //Status Summary
    widgetsData['statusSummary'][a.status] =
      (widgetsData['statusSummary'][a.status] ?? 0) + 1;

    // Total Program Funds
    // ! TODO : It will be fixed amount provided by the client
    // widgetsData['totalProgramFunds']
    const {
      willResultRhu,
      affordableHousing,
      createNewJob,
      ahCount,
      rhuCount,
      jobClassification,
    } = applicationData?.step4?.section1;
    const { buildingCount } = applicationData?.step1?.section2;

    if (a.status === 'APPROVED') {
      // Total Buildings
      widgetsData['totalBuildings'] += buildingCount ?? 0;

      // Total Approved Project
      widgetsData['totalApprovedProjects'] += 1;

      // Total Value Of All Projects
      widgetsData['totalValueOfAllProjects'] +=
        applicationData?.step3?.section2.reduce((acc: any, curr: any) => {
          return (acc += curr ? curr?.totalCost : 0);
        }, 0);

      // Total Blighted Properties Eliminated
      widgetsData['blightedPropertiesEliminated'] +=
        applicationData?.step4?.section3?.q3 === 'yes' ? 1 : 0;

      // Total Properties Returned To Use
      if (applicationData?.step4?.section3?.q1 === 'yes')
        widgetsData['totalPropertiesReturnedToUse'] +=
          applicationData?.step1?.section2?.buildingCount;

      // Total Housing Units Created
      widgetsData['totalHousingUnitsCreated'] +=
        willResultRhu === 'yes' ? rhuCount : 0;

      // Total Affordable Units Created
      widgetsData['totalAffordableUnitsCreated'] +=
        affordableHousing === 'yes' ? ahCount : 0;

      // Total New Jobs Created
      widgetsData['totalNewJobsCreated'] +=
        createNewJob === 'yes'
          ? jobClassification.length > 0 &&
            jobClassification.reduce((acc: any, curr: any) => {
              return (acc += curr?.noOfJobs);
            }, 0)
          : 0;

      // Total Program Funds Remaining
      const source = applicationData?.step3?.section2.find(
        (f: any) => f?.source === 'Façade Program Assistance'
      );
      const totalCost = source ? source?.totalCost : 0;
      widgetsData['totalProgramFundsRemaining'] +=
        widgetsData['totalAmountRequested'] - totalCost;

      //New Job Created By Classification
      if (jobClassification.length > 0) {
        jobClassification?.forEach((job: any) => {
          if (job?.title) {
            widgetsData['newJobsCreatedByClassification'][job?.title] =
              (widgetsData['newJobsCreatedByClassification'][job?.title] ?? 0) +
              job?.noOfJobs;
          }
        });
      }
    }
  });
  const [viewStatus, setViewStatus] = useState<string>('ALL');
  return (
    <>
      <div className='max-w-screen-3xl mx-auto'>
        <div className='font-semibold text-bold py-4 rounded-md space-x-2'>
          <span
            className={`${
              viewStatus === 'ALL'
                ? 'bg-primaryColor-normal'
                : 'bg-neutralColor-light hover:bg-primaryColor-normal'
            } text-white bg-primaryColor-normal px-4 py-2 cursor-pointer`}
            onClick={() => setViewStatus('ALL')}
          >
            All Applications
          </span>
          <span
            className={`${
              viewStatus === 'APPROVED'
                ? 'bg-primaryColor-normal'
                : 'bg-neutralColor-light hover:bg-primaryColor-normal'
            } text-white bg-primaryColor-normal px-4 py-2 cursor-pointer`}
            onClick={() => setViewStatus('APPROVED')}
          >
            Approved Applications
          </span>
        </div>

        <div className='my-4'>
          {/* <Card title='Project Location'>
            {false && (
              <div className='w-full flex justify-center items-center h-screen/2'>
                <div className='flex justify-center items-center gap-2 text-primaryColor-normal'>
                  <LocationMarkerIcon className='h-8 w-8' />
                  <h1>No Projects</h1>
                </div>
                <p>There are no assigned projects to display.</p>
              </div>
            )}
            {true && (
              <div className='w-full grid grid-cols-6 h-screen/2'>
                <div className='col-span-6 w-full border-l p-5'>
                  <MapGoogle projectList={dashboardData?.project_location} />
                </div>
              </div>
            )}
          </Card> */}
        </div>
        {viewStatus === 'ALL' && (
          <>
            <div className='my-4'>
              <section className='my-4 grid md:grid-cols-2 xl:grid-cols-4 gap-4'>
                <TotalApplicationsWidget
                  count={widgetsData?.totalApplications ?? 0}
                />
                <TotalAmountRequested
                  count={widgetsData?.totalAmountRequested ?? 0}
                />
                <TotalProgramFundsWidget
                  count={widgetsData?.totalProgramFunds ?? 0}
                />
              </section>
              <Card title='Status Summary'>
                <>
                  <div className='grid grid-cols-8 gap-6 py-6 px-6'>
                    {!isObjectEmpty(EApplicationStatus) &&
                      Object.keys(EApplicationStatus).map((s: any) => (
                        <div className='col-span-2 font-medium flex justify-between gap-6'>
                          <span>
                            {convertEnumToText(
                              EApplicationStatus[
                                s as keyof typeof EApplicationStatus
                              ]
                            )}
                          </span>
                          <span>{widgetsData['statusSummary'][s] ?? 0}</span>
                        </div>
                      ))}
                  </div>
                </>
              </Card>
            </div>
          </>
        )}
        {viewStatus === 'APPROVED' && (
          <>
            <section className='my-4 grid md:grid-cols-2 xl:grid-cols-4 gap-4'>
              <TotalBuildingsWidget count={widgetsData?.totalBuildings ?? 0} />
              <ApprovedProjectsWidget
                count={widgetsData?.totalApprovedProjects ?? 0}
              />
              <TotalProgramFundsRemainingWidget
                count={widgetsData?.totalProgramFundsRemaining ?? 0}
              />
              <TotalValueOfAllProjectsWidget
                count={widgetsData?.totalValueOfAllProjects ?? 0}
              />
              <BlightedPropertiesEliminatedWidget
                count={widgetsData?.blightedPropertiesEliminated ?? 0}
              />
              <TotalPropertiesReturnedToUseWidget
                count={widgetsData?.totalPropertiesReturnedToUse ?? 0}
              />
              <HousingUnitsCreatedWidget
                count={widgetsData?.totalHousingUnitsCreated ?? 0}
              />
              <AffordableUnitsCreatedWidget
                count={widgetsData?.totalAffordableUnitsCreated ?? 0}
              />
              <NewJobCreatedWidget
                count={widgetsData?.totalNewJobsCreated ?? 0}
              />
            </section>
            <section className='grid md:grid-cols-2 xl:grid-cols-2 gap-4'>
              <NewJobCreatedByClassification
                jobs={widgetsData?.newJobsCreatedByClassification}
              />
            </section>
          </>
        )}
      </div>
    </>
  );
};
